import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, X, Search, UserPlus, UserMinus, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import Checkbox from './Checkbox';
import { getOrgIcon, getOrgColorClass } from './orgStyles';

const API_BASE = import.meta.env.VITE_API_URL || '';

/** Foydalanuvchi ismini joriy tilda qaytaradi: "Familiya Ism" yoki login. */
function displayName(u, lang) {
    const l = lang === 'en' ? 'en' : lang === 'uz' ? 'uz' : 'ru';
    const last = u[`last_name_${l}`] || u.last_name_ru || u.last_name_uz || u.last_name_en || '';
    const first = u[`first_name_${l}`] || u.first_name_ru || u.first_name_uz || u.first_name_en || '';
    return `${last} ${first}`.trim() || u.login;
}

function displayPosition(u, lang) {
    const l = lang === 'en' ? 'en' : lang === 'uz' ? 'uz' : 'ru';
    return u[`position_${l}`] || u.position_ru || u.position_uz || u.position_en || '';
}

function matches(u, q, lang) {
    if (!q) return true;
    const s = q.toLowerCase();
    return displayName(u, lang).toLowerCase().includes(s) || (u.login || '').toLowerCase().includes(s);
}

function Avatar({ u }) {
    const letter = (u.last_name_ru || u.last_name_en || u.login || '?').charAt(0).toUpperCase();
    return (
        <div className="w-9 h-9 flex-shrink-0 rounded-full bg-brand/10 text-brand flex items-center justify-center text-sm font-bold overflow-hidden">
            {u.show_photo && u.photo
                ? <img src={`${API_BASE}${u.photo}`} alt="" className="w-full h-full object-cover" />
                : letter}
        </div>
    );
}

function UserRow({ u, lang, right, onClick, highlighted }) {
    const { t } = useTranslation();
    const position = displayPosition(u, lang);
    return (
        <div
            onClick={onClick}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${onClick ? 'cursor-pointer' : ''} ${highlighted ? 'bg-brand/5' : 'hover:bg-surface-2'}`}
        >
            <Avatar u={u} />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-primary truncate">{displayName(u, lang)}</span>
                    {u.is_superadmin && (
                        <ShieldCheck className="w-3.5 h-3.5 text-brand flex-shrink-0" title={t('organizations.members_superadmin_hint')} />
                    )}
                </div>
                <div className="text-xs text-muted truncate">{position ? `${position} · ` : ''}@{u.login}</div>
            </div>
            {right}
        </div>
    );
}

/**
 * Tashkilot a'zolarini boshqarish oynasi (Super Admin):
 * biriktirilgan foydalanuvchilarni ko'rish, chiqarish va yangilarini qo'shish.
 */
export default function OrgUsersModal({ org, onClose }) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'uz';
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState('members');
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [busy, setBusy] = useState(new Set());
    const [adding, setAdding] = useState(false);
    const [changed, setChanged] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/organizations/${org.id}/users`)
            .then(res => { if (!cancelled) setUsers(res.data || []); })
            .catch(err => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [org.id]);

    const members = useMemo(() => users.filter(u => u.is_member), [users]);
    const others = useMemo(() => users.filter(u => !u.is_member), [users]);
    const list = (tab === 'members' ? members : others).filter(u => matches(u, search, lang));

    const setMember = (ids, value) =>
        setUsers(prev => prev.map(u => (ids.includes(u.id) ? { ...u, is_member: value } : u)));
    const markBusy = (id, on) =>
        setBusy(prev => { const n = new Set(prev); on ? n.add(id) : n.delete(id); return n; });

    async function removeUser(u) {
        markBusy(u.id, true);
        setError(null);
        try {
            await api.delete(`/organizations/${org.id}/users/${u.id}`);
            setMember([u.id], false);
            setChanged(true);
        } catch (err) {
            setError(err.message || t('organizations.members_error'));
        } finally {
            markBusy(u.id, false);
        }
    }

    async function addUsers(ids) {
        if (ids.length === 0) return;
        setAdding(true);
        ids.forEach(id => markBusy(id, true));
        setError(null);
        try {
            await api.post(`/organizations/${org.id}/users`, { user_ids: ids });
            setMember(ids, true);
            setSelected(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
            setChanged(true);
        } catch (err) {
            setError(err.message || t('organizations.members_error'));
        } finally {
            ids.forEach(id => markBusy(id, false));
            setAdding(false);
        }
    }

    const toggleSelect = (id) =>
        setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const visibleIds = list.map(u => u.id);
    const allVisibleSelected = tab === 'others' && visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
    const someVisibleSelected = tab === 'others' && visibleIds.some(id => selected.has(id));
    const toggleSelectAllVisible = () =>
        setSelected(prev => {
            const n = new Set(prev);
            if (allVisibleSelected) visibleIds.forEach(id => n.delete(id));
            else visibleIds.forEach(id => n.add(id));
            return n;
        });

    const OrgIcon = getOrgIcon(org);
    const orgName = (lang === 'en' ? org.name_en : lang === 'ru' ? org.name_ru : org.name_uz) || org.name_uz;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(changed); }}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 8 }}
                transition={{ duration: 0.15 }}
                className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-xl flex flex-col h-[min(85vh,720px)] overflow-hidden"
            >
                {/* Header */}
                <div className="p-5 border-b border-border flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border ${getOrgColorClass(org)}`}>
                        {org.logo_url ? <img src={org.logo_url} alt="" className="w-6 h-6 object-contain" /> : <OrgIcon className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-base font-bold text-primary flex items-center gap-2">
                            <Users className="w-4 h-4 text-brand" />
                            {t('organizations.members_title')} — {orgName}
                        </h2>
                        <p className="text-xs text-muted mt-0.5">{t('organizations.members_subtitle')}</p>
                    </div>
                    <button
                        id="org-users-close"
                        onClick={() => onClose(changed)}
                        className="p-1.5 rounded-lg text-muted hover:text-primary hover:bg-surface-2 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tabs + search */}
                <div className="px-5 pt-4 space-y-3">
                    <div className="grid grid-cols-2 p-1 rounded-xl bg-surface-2 border border-border">
                        {[
                            { key: 'members', label: t('organizations.members_tab_members'), count: members.length },
                            { key: 'others', label: t('organizations.members_tab_others'), count: others.length },
                        ].map(({ key, label, count }) => (
                            <button
                                key={key}
                                id={`org-users-tab-${key}`}
                                onClick={() => setTab(key)}
                                className={`relative py-2 text-xs font-semibold rounded-lg transition-colors ${tab === key ? 'text-inverse' : 'text-muted hover:text-primary'}`}
                            >
                                {tab === key && (
                                    <motion.span layoutId="org-users-tab-pill" className="absolute inset-0 rounded-lg bg-brand shadow-brand-sm" transition={{ type: 'spring', bounce: 0.15, duration: 0.35 }} />
                                )}
                                <span className="relative z-10">{label} <span className="opacity-70">({count})</span></span>
                            </button>
                        ))}
                    </div>
                    <div className="relative">
                        <Search className="w-4 h-4 text-muted absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            id="org-users-search"
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('organizations.members_search')}
                            className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-surface-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                        />
                    </div>
                    {tab === 'others' && list.length > 0 && (
                        <div className="flex items-center justify-between px-3">
                            <label className="flex items-center gap-2 text-xs text-muted cursor-pointer select-none" onClick={toggleSelectAllVisible}>
                                <Checkbox checked={allVisibleSelected} indeterminate={!allVisibleSelected && someVisibleSelected} onChange={toggleSelectAllVisible} />
                                {t('organizations.select_all')}
                            </label>
                            {selected.size > 0 && (
                                <span className="text-xs font-semibold text-brand">{t('organizations.members_selected', { count: selected.size })}</span>
                            )}
                        </div>
                    )}
                </div>

                {/* List */}
                <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3">
                    {loading ? (
                        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-brand animate-spin" /></div>
                    ) : list.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-14 text-center">
                            <Users className="w-10 h-10 text-muted opacity-30 mb-2" />
                            <p className="text-sm text-muted">{t('organizations.members_empty')}</p>
                        </div>
                    ) : (
                        <AnimatePresence initial={false}>
                            {list.map(u => (
                                <motion.div key={u.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.15 }}>
                                    {tab === 'members' ? (
                                        <UserRow
                                            u={u}
                                            lang={lang}
                                            right={
                                                <button
                                                    id={`org-user-remove-${u.id}`}
                                                    onClick={() => removeUser(u)}
                                                    disabled={busy.has(u.id)}
                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-error bg-error/10 hover:bg-error hover:text-inverse transition-colors disabled:opacity-50"
                                                >
                                                    {busy.has(u.id) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
                                                    {t('organizations.members_remove')}
                                                </button>
                                            }
                                        />
                                    ) : (
                                        <UserRow
                                            u={u}
                                            lang={lang}
                                            highlighted={selected.has(u.id)}
                                            onClick={() => toggleSelect(u.id)}
                                            right={
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        id={`org-user-add-${u.id}`}
                                                        onClick={(e) => { e.stopPropagation(); addUsers([u.id]); }}
                                                        disabled={busy.has(u.id)}
                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-brand bg-brand/10 hover:bg-brand hover:text-inverse transition-colors disabled:opacity-50"
                                                    >
                                                        {busy.has(u.id) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                                                        {t('organizations.members_add')}
                                                    </button>
                                                    <Checkbox checked={selected.has(u.id)} onChange={() => toggleSelect(u.id)} />
                                                </div>
                                            }
                                        />
                                    )}
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    )}
                </div>

                {/* Footer */}
                {(error || (tab === 'others' && selected.size > 0)) && (
                    <div className="p-4 border-t border-border flex items-center gap-3">
                        {error && (
                            <div className="flex-1 flex items-center gap-2 text-xs text-error min-w-0">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">{error}</span>
                            </div>
                        )}
                        {tab === 'others' && selected.size > 0 && (
                            <button
                                id="org-users-add-selected"
                                onClick={() => addUsers([...selected])}
                                disabled={adding}
                                className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand text-inverse text-sm font-semibold hover:bg-brand/90 shadow-brand-sm transition-all disabled:opacity-50"
                            >
                                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                                {t('organizations.members_add_selected')} ({selected.size})
                            </button>
                        )}
                    </div>
                )}
            </motion.div>
        </div>
    );
}

/**
 * Yangi tashkilot yaratishda foydalanuvchilarni tanlash uchun ixcham ro'yxat.
 * @param {{ value: string[], onChange: (ids: string[]) => void, excludeIds?: string[] }} props
 */
export function OrgUserPicker({ value, onChange, excludeIds = [] }) {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'uz';
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    useEffect(() => {
        api.get('/admin/users')
            .then(res => setUsers((res.data || []).filter(u => u.is_active !== false && !excludeIds.includes(u.id))))
            .catch(() => setUsers([]))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selected = new Set(value);
    const list = users.filter(u => matches(u, search, lang));
    const toggle = (id) => {
        const n = new Set(selected);
        n.has(id) ? n.delete(id) : n.add(id);
        onChange([...n]);
    };

    return (
        <div className="rounded-xl border border-border bg-surface-2 overflow-hidden">
            <div className="p-2 border-b border-border flex items-center gap-2">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 text-muted absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        id="org-create-users-search"
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('organizations.members_search')}
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                    />
                </div>
                {value.length > 0 && (
                    <span className="text-xs font-semibold text-brand whitespace-nowrap px-2">{t('organizations.members_selected', { count: value.length })}</span>
                )}
            </div>
            <div className="max-h-56 overflow-y-auto p-1.5">
                {loading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 text-brand animate-spin" /></div>
                ) : list.length === 0 ? (
                    <p className="text-xs text-muted text-center py-6">{t('organizations.members_empty')}</p>
                ) : list.map(u => (
                    <UserRow
                        key={u.id}
                        u={u}
                        lang={lang}
                        highlighted={selected.has(u.id)}
                        onClick={() => toggle(u.id)}
                        right={<Checkbox checked={selected.has(u.id)} onChange={() => toggle(u.id)} />}
                    />
                ))}
            </div>
        </div>
    );
}
